import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "@/components/common/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, Zap, Package, Rocket, Building2, Crown, Factory, Car, ShieldCheck, ShoppingCart, HardDrive, Sparkles, Mail } from "lucide-react";
import { fmtINR } from "@/lib/format";

const PLAN_ICONS = { solo: Zap, starter: Package, growth: Rocket, professional: Building2, enterprise: Crown };
const SEAT_ICONS = { manufacturing: Factory, dealership: Car, compliance_dsc: ShieldCheck };
const PACK_ICONS = { storage_25gb: HardDrive, ai_1000: Sparkles, invoices_1000: ShoppingCart, emails_1000: Mail };

function Bar({ used, cap, label, display, danger = 80 }) {
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const tone = pct >= 100 ? "bg-red-500" : pct >= danger ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div data-testid={`usage-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase text-muted-foreground">
        <span>{label}</span><span>{pct}%</span>
      </div>
      <div className="text-sm font-bold mt-0.5">{display}</div>
      <div className="w-full h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Subscription() {
  const [catalog, setCatalog] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [c, u] = await Promise.all([api.get("/subscription/catalog"), api.get("/subscription/usage")]);
    setCatalog(c.data); setUsage(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const buyPack = async (packId) => {
    setBusy(true);
    try {
      await api.post(`/subscription/packs/${packId}`);
      toast.success("Pack applied. Charge will appear on your next invoice.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const changePlan = async (planId) => {
    if (!window.confirm(`Switch to the ${catalog.plans[planId].name} plan?`)) return;
    setBusy(true);
    try {
      await api.post("/subscription/plan", { plan: planId });
      toast.success("Plan switched");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!catalog || !usage) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const currentPlan = catalog.plans[usage.plan] || catalog.plans.starter;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="subscription-page">
      <PageHeader eyebrow="Billing" title="Subscription & Usage"
        description="Pick the plan that fits your team, add module seats, top-up when you hit a cap." />

      {/* Current usage widget */}
      <Card className="p-5 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Current plan</div>
            <div className="text-2xl font-bold mt-0.5">{currentPlan.name} · {fmtINR(currentPlan.price_inr)}/mo</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Billing month</div>
            <div className="font-mono">{usage.month}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Bar label="Seats" used={usage.seats.used} cap={usage.seats.bundled}
            display={`${usage.seats.used} / ${usage.seats.bundled} bundled${usage.seats.extras ? ` · +${usage.seats.extras} extra` : ""}`} />
          <Bar label="Storage" used={usage.storage.pct} cap={100}
            display={`${(usage.storage.used_bytes / (1024**3)).toFixed(2)} / ${usage.storage.cap_gb} GB`} />
          <Bar label="AI queries" used={usage.ai_queries.used} cap={usage.ai_queries.cap}
            display={`${usage.ai_queries.used} / ${usage.ai_queries.cap}`} />
          <Bar label="Emails" used={usage.email.used} cap={usage.email.cap}
            display={`${usage.email.used} / ${usage.email.cap === 999999 ? "∞" : usage.email.cap}`} />
        </div>
        {usage.seats.extras > 0 && (
          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
            Extra-seat charge this month: <b className="text-foreground">{fmtINR(usage.seats.extra_cost_inr)}</b> · billed with your next invoice.
          </div>
        )}
      </Card>

      <Tabs defaultValue="plans">
        <TabsList className="rounded-sm">
          <TabsTrigger value="plans" className="rounded-sm">Plans</TabsTrigger>
          <TabsTrigger value="seats" className="rounded-sm">Module seats</TabsTrigger>
          <TabsTrigger value="packs" className="rounded-sm">Top-up packs</TabsTrigger>
          <TabsTrigger value="infra" className="rounded-sm">Infrastructure</TabsTrigger>
        </TabsList>

        {/* PLANS */}
        <TabsContent value="plans" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            {Object.values(catalog.plans).map((p) => {
              const Icon = PLAN_ICONS[p.id] || Package;
              const active = usage.plan === p.id;
              return (
                <Card key={p.id} data-testid={`plan-${p.id}`}
                  className={`p-5 rounded-sm relative flex flex-col ${active ? "border-accent ring-1 ring-accent" : ""}`}>
                  {active && <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Active</div>}
                  <Icon className="w-6 h-6 text-accent mb-3" />
                  <div className="font-heading font-bold text-xl">{p.name}</div>
                  <div className="mt-2 font-mono">
                    <span className="text-2xl">{fmtINR(p.price_inr)}</span>
                    <span className="text-xs text-muted-foreground font-sans"> /mo</span>
                  </div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mt-1">
                    {p.bundled_users} bundled seats · +₹{p.extra_user_inr}/extra
                  </div>
                  <ul className="mt-4 space-y-1.5 text-xs flex-1">
                    {p.features.slice(0, 6).map((f) => (
                      <li key={f} className="flex gap-1.5"><CheckCircle2 className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                        <span className="text-muted-foreground leading-snug">{f}</span></li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t text-[10px] font-mono text-muted-foreground">
                    {p.storage_gb} GB · {p.ai_queries_month.toLocaleString()} AI · {p.email_month === 999999 ? "∞" : p.email_month.toLocaleString()} email/mo
                  </div>
                  {!active && (
                    <Button onClick={() => changePlan(p.id)} disabled={busy}
                      className="w-full mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid={`switch-${p.id}`}>
                      Switch to {p.name}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* MODULE SEATS */}
        <TabsContent value="seats" className="mt-5">
          <div className="text-sm text-muted-foreground mb-4">
            Add specialised seats on top of your base plan. Base accounting stays on the main plan — these seats add module-specific access per user.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.values(catalog.addon_seats).map((s) => {
              const Icon = SEAT_ICONS[s.id] || Package;
              return (
                <Card key={s.id} className="p-5 rounded-sm" data-testid={`seat-${s.id}`}>
                  <Icon className="w-6 h-6 text-accent mb-3" />
                  <div className="font-bold text-lg">{s.name}</div>
                  <div className="mt-1 font-mono text-xl">{fmtINR(s.price_inr_per_user)}<span className="text-xs text-muted-foreground font-sans"> /user/mo</span></div>
                  <div className="text-sm text-muted-foreground mt-3">{s.description}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {s.modules.slice(0, 6).map((m) => (
                      <span key={m} className="text-[9px] font-mono uppercase px-1.5 py-0.5 border border-accent/40 text-accent rounded-sm">{m}</span>
                    ))}
                  </div>
                  <Button className="w-full mt-4 rounded-sm" variant="outline"
                    onClick={() => toast.info("Contact platform ops to enable seat billing — Razorpay auto-charge coming soon.")}
                    data-testid={`seat-cta-${s.id}`}>Request seats</Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* TOP-UP PACKS */}
        <TabsContent value="packs" className="mt-5">
          <div className="text-sm text-muted-foreground mb-4">Hit your monthly cap? Buy a top-up pack — applied instantly to the current month.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {Object.values(catalog.overage_packs).map((p) => {
              const Icon = PACK_ICONS[p.id] || Package;
              return (
                <Card key={p.id} className="p-4 rounded-sm" data-testid={`pack-${p.id}`}>
                  <Icon className="w-6 h-6 text-accent mb-2" />
                  <div className="font-bold">{p.name}</div>
                  <div className="mt-1 font-mono text-xl">{fmtINR(p.price_inr)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{p.recurring ? "Recurring monthly" : "One-time this month"}</div>
                  <Button onClick={() => buyPack(p.id)} disabled={busy}
                    className="w-full mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white text-xs" data-testid={`buy-${p.id}`}>
                    Buy now
                  </Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* INFRA ADD-ONS */}
        <TabsContent value="infra" className="mt-5">
          <div className="text-sm text-muted-foreground mb-4">
            Enterprise-grade infrastructure add-ons — dedicated isolation, guaranteed performance, financial SLAs.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.values(catalog.infra_addons).map((a) => (
              <Card key={a.id} className={`p-5 rounded-sm ${a.id === "sovereign_stack" ? "border-accent" : ""}`} data-testid={`infra-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-lg">{a.name}</div>
                    <div className="mt-1 font-mono text-xl">{fmtINR(a.price_inr_month)}<span className="text-xs text-muted-foreground font-sans"> /mo</span></div>
                  </div>
                  {a.id === "sovereign_stack" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-accent text-white rounded-sm">Bundle · Save ₹7K</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-3">{a.description}</div>
                <Button className="w-full mt-4 rounded-sm" variant="outline"
                  onClick={() => toast.info("Book a call with the Platform-Ops team to provision. It's not self-serve.")}
                  data-testid={`infra-cta-${a.id}`}>Talk to sales</Button>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
